import { usePage } from '@inertiajs/react'
import { useCallback, useEffect, useState } from 'react'

export type Appearance = 'light' | 'dark'

const STORAGE_KEY = 'appearance'
const COOKIE_KEY = 'appearance'

function prefersDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

function defaultAppearance(): Appearance {
  if (typeof window === 'undefined') return 'light'
  return (localStorage.getItem(STORAGE_KEY) as Appearance | null) ?? (prefersDark() ? 'dark' : 'light')
}

function applyTheme(appearance: Appearance) {
  document.documentElement.classList.toggle('dark', appearance === 'dark')
}

export function initializeTheme() {
  if (typeof window === 'undefined') return
  applyTheme(defaultAppearance())
}

export function useAppearance() {
  // Start from the server's value (ApplicationController reads the same
  // cookie updateAppearance writes below), not from localStorage — the
  // server can't read localStorage, so starting from it here would make the
  // server and browser show different themes at first. The one gap: a
  // first-time visitor has no cookie yet, so the server defaults to 'light'
  // even if their OS prefers dark. The effect below fixes that after mount.
  const shared = usePage<{ appearance?: Appearance }>().props.appearance
  const [appearance, setAppearance] = useState<Appearance>(shared ?? 'light')

  useEffect(() => {
    if (shared) return
    setAppearance(defaultAppearance())
  }, [shared])

  const updateAppearance = useCallback((mode: Appearance) => {
    setAppearance(mode)
    localStorage.setItem(STORAGE_KEY, mode)
    const secure = location.protocol === 'https:' ? '; secure' : ''
    document.cookie = `${COOKIE_KEY}=${mode}; path=/; max-age=31536000; samesite=lax${secure}`
    applyTheme(mode)
  }, [])

  return { appearance, updateAppearance } as const
}
